import {Component, inject, OnInit} from '@angular/core';
import {PermissionService} from "../../../_auth/permission.service";
import {EmploymentRelationshipHttpService} from "../x-http-requests/employment-relationship-http.service";
import {EmploymentRelationshipDto} from "../x-models/employment-relationship-dto";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {AppRoutes} from "../../../enums/routes";
import {Router, RouterLink} from "@angular/router";
import {CompanyRoutes} from "../x-models/x-enums/company-routes";
import {Side} from "../x-models/x-enums/side";
import {CompanyTimeZoneService} from "../../../_shared/time/company-time-zone.service";
import {companyToday, DayKey, endOfMonth, isDayKey} from "../../../_shared/time/company-day";
import {DownloadInformation} from "../../x-components/x-models/download-information";
import {EmploymentRelationshipService} from "../_services/_dto-services/employment-relationship.service";
import {ServerEndpoints} from "../../../server.endpoints";
import {FormsModule} from "@angular/forms";
import {CommonModule} from "@angular/common";
import {LoadingComponent} from "../../x-components/loading/loading.component";
import {DownloadComponent} from "../../x-components/download/download.component";

@Component({
  selector: 'app-employee-relationships',
  templateUrl: './employment-relationships.component.html',
  imports: [
    CommonModule,
    FormsModule,
    LoadingComponent,
    DownloadComponent,
    RouterLink
  ],
  styleUrls: ['./employment-relationships.component.scss']
})
export class EmploymentRelationshipsComponent implements OnInit {
  // relationships: EmploymentRelationshipDto[] | undefined;
  relationships_filtered: EmploymentRelationshipDto[] | undefined;
  er_name: string = "";
  er_salary: number = 12;
  er_limit: number | undefined;
  er_multiplier: number | undefined;
  selectionMode: boolean = false;
  /**
   * The payroll export ("Zeiten exportieren") range: company calendar days (`yyyy-MM-dd`), BOTH inclusive, sent
   * verbatim. The server resolves them in the company zone and computes the exclusive end itself (#648). The old
   * client added a day to "Bis" and the server added another, so the export also contained the day AFTER "Bis" —
   * and the picker, reading the shifted value back, displayed that next day too.
   */
  start: DayKey = '';
  end: DayKey = '';
  private readonly companyTimeZone = inject(CompanyTimeZoneService);
  downloadInformation: DownloadInformation = new DownloadInformation();
  downloadParams: string = "";
  downloadData: any = [];
  protected readonly Routes = AppRoutes;
  protected readonly Side = Side;
  protected readonly CompanyRoutes = CompanyRoutes;
  protected readonly ServerEndpoints = ServerEndpoints;

  constructor(public PermissionService: PermissionService, private employmentRelationshipHttpService: EmploymentRelationshipHttpService,
              private globalAlertService: GlobalAlertService, private router: Router,
              public EmploymentRelationshipService: EmploymentRelationshipService) {
    this.EmploymentRelationshipService.EntitiesChanged.subscribe({
      next: value => {
        this.Filter();
      }
    })
  }

  ngOnInit(): void {
    // Both ends default to today in the company zone, and the export link carries exactly what the pickers show.
    this.companyTimeZone.load().subscribe(timeZone => {
      this.start = companyToday(timeZone);
      this.end = this.start;
      this.updateDownloadParams();
      this.zoneLoaded = true;
    });

    // this.EmploymentRelationshipService.LoadData();
  }

  Filter() {
    this.relationships_filtered = Object.assign([], this.EmploymentRelationshipService.Entities?.filter(x => this.er_name == "" || x.name.toLowerCase().indexOf(this.er_name.toLowerCase()) > -1));
  }

  Add() {
    this.employmentRelationshipHttpService.add(this.er_name, this.er_salary, this.er_limit, this.er_multiplier ?? 1).subscribe(x => {
      this.er_name = "";
      this.EmploymentRelationshipService.Replace(x);
      this.Filter();
      this.globalAlertService.createAlertBannerModel("Hinzugefügt", "Arbeitsverhältnis erfolgreich hinzugefügt.", AlertLevel.success, 2000);
      this.globalAlertService.show();
    }, error => {
      this.globalAlertService.createAlertBannerModel("Fehler", "Beim Hinzufügen ist ein Fehler aufgetreten. Bitte Werte überprüfen.", AlertLevel.error, 3000);
      this.globalAlertService.show();
    })
  }

  redirectToSettings(id: string) {
    this.router.navigate(["/" + AppRoutes.CompanyModule + "/" + CompanyRoutes.EmployeeRelationshipsSettings], {queryParams: {id: id}});
  }

  enterSelectionMode() {
    this.selectionMode = true;
  }

  leaveSelectionMode() {
    this.selectionMode = false;
  }

  /**
   * A picked day. "Von" also moves "Bis" to the end of that month (the usual monthly payroll run); a cleared input
   * falls back to today in the company zone.
   */
  setDate(value: string, side: Side) {
    const day = isDayKey(value) ? value : companyToday(this.companyTimeZone.timeZone());
    switch (side) {
      case Side.Left:
        this.start = day;
        this.end = endOfMonth(day);
        break;
      case Side.Right:
        this.end = day;
        break;
    }
    this.updateDownloadParams();
  }

  private updateDownloadParams() {
    // Pass the SPA's own origin so the export can build "Formulare" hyperlinks that point at the correct
    // (possibly white-label / multi-tenant) web app, not the API origin.
    this.downloadParams = `start=${this.start}&end=${this.end}`
      + `&webOrigin=${encodeURIComponent(window.location.origin)}`
  }

  /** The day a picker shows: exactly the stored day, never a shifted one. */
  getDate(side: Side): string {
    switch (side) {
      case Side.Left:
        return this.start
      case Side.Right:
        return this.end
    }
  }

  setDownloadData() {
    this.downloadData = this.EmploymentRelationshipService.GetSelectedIds() ?? [];
  }

  /** A real range: both ends are calendar days and "Von" is not after "Bis". Empty values are NOT valid. */
  areValidDates(): boolean {
    return isDayKey(this.start) && isDayKey(this.end) && this.start <= this.end;
  }

  /** Set once the company zone has answered and the default range is filled in (see `ngOnInit`). */
  private zoneLoaded = false;

  /**
   * Whether "Zeiten exportieren" may be offered. Until the zone answers both dates are `''`, and an early click sent
   * `start=&end=` (a 400 from the export); an inverted or cleared range must not be exported either.
   */
  get canExport(): boolean {
    return this.zoneLoaded && this.areValidDates();
  }
}
